import * as XLSX from 'xlsx';

/**
 * Exports reference lists to Excel with values and synonyms
 * @param {Array} lists - Array of reference lists from view
 * @param {Array} values - Array of all reference values
 * @param {Array} synonyms - Array of all reference value synonyms
 * @returns {void} Triggers download in browser
 */
export function exportReferenceLists(lists, values = [], synonyms = []) {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Reference Lists
  const listHeaders = [
    'List Name',
    'Description',
    'Category',
    'Status',
    'Total Values',
    'Used By Elements'
  ];

  const listRows = lists.map(list => ({
    'List Name': list.list_name || '',
    'Description': list.description || '',
    'Category': list.category || '',
    'Status': list.status || '',
    'Total Values': list.total_values || 0,
    'Used By Elements': list.usage_count || 0
  }));

  const wsLists = XLSX.utils.json_to_sheet(listRows, { header: listHeaders });
  wsLists['!cols'] = [
    { wch: 30 }, // List Name
    { wch: 50 }, // Description
    { wch: 20 }, // Category
    { wch: 15 }, // Status
    { wch: 15 }, // Total Values
    { wch: 18 }  // Used By Elements
  ];
  XLSX.utils.book_append_sheet(wb, wsLists, 'Reference Lists');

  // Sheet 2: Values
  const valueHeaders = [
    'List Name',
    'Canonical Value',
    'Description',
    'Is Active'
  ];

  // Create a map of list id to list name
  const listMap = {};
  lists.forEach(list => {
    listMap[list.id] = list.list_name;
  });

  const valueRows = values.map(value => ({
    'List Name': listMap[value.list_id] || value.list_name || '',
    'Canonical Value': value.canonical_value || '',
    'Description': value.value_description || '',
    'Is Active': value.is_active ? 'Yes' : 'No'
  }));

  const wsValues = XLSX.utils.json_to_sheet(valueRows, { header: valueHeaders });
  wsValues['!cols'] = [
    { wch: 30 }, // List Name
    { wch: 30 }, // Canonical Value
    { wch: 50 }, // Description
    { wch: 10 }  // Is Active
  ];
  XLSX.utils.book_append_sheet(wb, wsValues, 'Values');

  // Sheet 3: Synonyms
  const synonymHeaders = [
    'List Name',
    'Canonical Value',
    'Synonym',
    'Is Approved'
  ];

  // Create a map of value id to value info
  const valueMap = {};
  values.forEach(value => {
    valueMap[value.id] = {
      canonical_value: value.canonical_value,
      list_name: listMap[value.list_id] || value.list_name || ''
    };
  });

  const synonymRows = synonyms.map(synonym => ({
    'List Name': synonym.list_name || valueMap[synonym.reference_value_id]?.list_name || '',
    'Canonical Value': synonym.canonical_value || valueMap[synonym.reference_value_id]?.canonical_value || '',
    'Synonym': synonym.synonym || '',
    'Is Approved': synonym.is_approved ? 'Yes' : 'No'
  }));

  const wsSynonyms = XLSX.utils.json_to_sheet(synonymRows, { header: synonymHeaders });
  wsSynonyms['!cols'] = [
    { wch: 30 }, // List Name
    { wch: 30 }, // Canonical Value
    { wch: 30 }, // Synonym
    { wch: 12 }  // Is Approved
  ];
  XLSX.utils.book_append_sheet(wb, wsSynonyms, 'Synonyms');

  const date = new Date().toISOString().split('T')[0];
  const filename = `reference_lists_export_${date}.xlsx`;

  XLSX.writeFile(wb, filename);
}

/**
 * Exports data elements to Excel in the same format used for import
 * @param {Array} elements - Array of data elements from view
 * @returns {void} Triggers download in browser
 */
export function exportDataElements(elements) {
  const wb = XLSX.utils.book_new();

  // Define headers - all columns including version
  const headers = [
    'Pole_Star_Generic_Name',
    'Definition',
    'Field_Type',
    'Status',
    'Version',
    'Processing_Rules',
    'Processing_Language',
    'Reference_List'
  ];

  // Transform elements to export format
  // processing_rules can be an array of rule names or a comma-separated string
  const rows = elements.map(el => {
    let processingRules = '';
    if (el.processing_rules) {
      if (Array.isArray(el.processing_rules)) {
        processingRules = el.processing_rules.join(', ');
      } else {
        processingRules = el.processing_rules;
      }
    }
    return {
      Pole_Star_Generic_Name: el.canonical_name || '',
      Definition: el.definition || '',
      Field_Type: el.data_type || '',
      Status: el.status || '',
      Version: el.version_number || el.current_version_number || 1,
      Processing_Rules: processingRules,
      Processing_Language: el.processing_language || '',
      Reference_List: el.reference_list_name || ''
    };
  });

  // Create data sheet
  const wsData = XLSX.utils.json_to_sheet(rows, { header: headers });

  // Set column widths
  wsData['!cols'] = [
    { wch: 30 }, // Pole_Star_Generic_Name
    { wch: 50 }, // Definition
    { wch: 15 }, // Field_Type
    { wch: 15 }, // Status
    { wch: 10 }, // Version
    { wch: 50 }, // Processing_Rules
    { wch: 20 }, // Processing_Language
    { wch: 25 }  // Reference_List
  ];

  // Add data sheet to workbook
  XLSX.utils.book_append_sheet(wb, wsData, 'Data Dictionary');

  // Generate filename with current date
  const date = new Date().toISOString().split('T')[0];
  const filename = `data_dictionary_export_${date}.xlsx`;

  // Write and trigger download
  XLSX.writeFile(wb, filename);
}

/**
 * Generates a downloadable Excel template for data dictionary import
 * @returns {void} Triggers download in browser
 */
export function generateDictionaryTemplate() {
  // Create workbook
  const wb = XLSX.utils.book_new();

  // Define headers for the data sheet
  const headers = [
    'Pole_Star_Generic_Name',
    'Definition',
    'Field_Type',
    'Reference_List'
  ];

  // Create example rows
  const exampleRows = [
    {
      Pole_Star_Generic_Name: 'customer_id',
      Definition: 'Unique identifier for a customer',
      Field_Type: 'string',
      Reference_List: ''
    },
    {
      Pole_Star_Generic_Name: 'order_total',
      Definition: 'Total amount of the order including tax',
      Field_Type: 'decimal128',
      Reference_List: ''
    },
    {
      Pole_Star_Generic_Name: 'flag_country',
      Definition: 'Country of the vessel flag',
      Field_Type: 'string',
      Reference_List: 'Country'
    }
  ];

  // Create data sheet
  const wsData = XLSX.utils.json_to_sheet(exampleRows, { header: headers });

  // Set column widths
  wsData['!cols'] = [
    { wch: 30 }, // Pole_Star_Generic_Name
    { wch: 50 }, // Definition
    { wch: 15 }, // Field_Type
    { wch: 25 }  // Reference_List
  ];

  // Add data sheet to workbook
  XLSX.utils.book_append_sheet(wb, wsData, 'Data Dictionary');

  // Create instructions sheet
  const instructions = [
    ['Data Dictionary Import Template'],
    [''],
    ['Instructions:'],
    ['1. Fill in the data in the "Data Dictionary" sheet'],
    ['2. Do not modify the column headers'],
    ['3. Remove or replace the example rows with your actual data'],
    ['4. Save the file and upload it through the import dialog'],
    [''],
    ['Column Descriptions:'],
    [''],
    ['Pole_Star_Generic_Name (Required):'],
    ['  - The canonical name for the data element'],
    ['  - Must be unique across all elements'],
    ['  - Maximum 255 characters'],
    ['  - Use lowercase with underscores (e.g., customer_id)'],
    [''],
    ['Definition (Optional):'],
    ['  - Clear description of what this data element represents'],
    ['  - Maximum 5000 characters'],
    [''],
    ['Field_Type (Optional):'],
    ['  - The data type of this element'],
    ['  - Valid values: binary, binary_view, bool, bool8, date32, date64, decimal128, decimal256, dense_union, dictionary, duration, fixed_shape_tensor, float16, float32, float64, int8, int16, int32, int64, json, large_binary, large_list, large_list_view, large_string, list, list_view, map, month_day_nano_interval, null, opaque, run_end_encoded, sparse_union, string, string_view, struct, time32, time64, timestamp, uint8, uint16, uint32, uint64, union, uuid'],
    [''],
    ['Reference_List (Optional):'],
    ['  - Name of the reference list to associate with this element'],
    ['  - Must match an existing reference list name in the system'],
    ['  - Leave empty if no reference list applies'],
    [''],
    ['Notes:'],
    ['  - If a Pole_Star_Generic_Name already exists, it will be UPDATED'],
    ['  - Existing processing_logic and test_cases will be preserved'],
    ['  - Maximum file size: 10MB'],
    ['  - Maximum rows: 10,000 elements'],
    ['  - Only .xlsx and .xls files are supported']
  ];

  const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);

  // Set column width for instructions
  wsInstructions['!cols'] = [{ wch: 80 }];

  // Add instructions sheet to workbook
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

  // Generate filename with current date
  const date = new Date().toISOString().split('T')[0];
  const filename = `data_dictionary_template_${date}.xlsx`;

  // Write and trigger download
  XLSX.writeFile(wb, filename);
}

/**
 * Exports processing rules to Excel
 * @param {Array} rules - Array of processing rules from view
 * @returns {void} Triggers download in browser
 */
export function exportProcessingRules(rules) {
  const wb = XLSX.utils.book_new();

  // Define headers for export
  const headers = [
    'Rule_Name',
    'Description',
    'Rule_Type',
    'Rule_Language',
    'Input_Type',
    'Output_Type',
    'Rule_Code',
    'Test_Cases',
    'Status',
    'Version'
  ];

  // Transform rules to export format
  const rows = rules.map(rule => ({
    Rule_Name: rule.rule_name || '',
    Description: rule.description || '',
    Rule_Type: rule.rule_type || '',
    Rule_Language: rule.rule_language || '',
    Input_Type: rule.input_type || '',
    Output_Type: rule.output_type || '',
    Rule_Code: rule.rule_code || '',
    Test_Cases: rule.test_cases ? JSON.stringify(rule.test_cases) : '',
    Status: rule.status || '',
    Version: rule.version || 1
  }));

  // Create data sheet
  const wsData = XLSX.utils.json_to_sheet(rows, { header: headers });

  // Set column widths
  wsData['!cols'] = [
    { wch: 30 },  // Rule_Name
    { wch: 50 },  // Description
    { wch: 15 },  // Rule_Type
    { wch: 15 },  // Rule_Language
    { wch: 15 },  // Input_Type
    { wch: 15 },  // Output_Type
    { wch: 80 },  // Rule_Code
    { wch: 50 },  // Test_Cases
    { wch: 12 },  // Status
    { wch: 10 }   // Version
  ];

  // Add data sheet to workbook
  XLSX.utils.book_append_sheet(wb, wsData, 'Processing Rules');

  // Generate filename with current date
  const date = new Date().toISOString().split('T')[0];
  const filename = `processing_rules_export_${date}.xlsx`;

  // Write and trigger download
  XLSX.writeFile(wb, filename);
}

/**
 * Generates a downloadable Excel template for processing rules import
 * @returns {void} Triggers download in browser
 */
/**
 * Generates a downloadable Excel template for reference lists import
 * @returns {void} Triggers download in browser
 */
export function generateReferenceListsTemplate() {
  const wb = XLSX.utils.book_new();

  // Sheet 1: Reference Lists
  const listHeaders = [
    'List Name',
    'Description',
    'Category',
    'Status'
  ];

  const listExampleRows = [
    {
      'List Name': 'country_codes',
      'Description': 'ISO 3166-1 alpha-2 country codes',
      'Category': 'Geographic',
      'Status': 'Active'
    },
    {
      'List Name': 'currency_codes',
      'Description': 'ISO 4217 currency codes',
      'Category': 'Financial',
      'Status': 'Active'
    },
    {
      'List Name': 'vessel_types',
      'Description': 'Classification of vessel types',
      'Category': 'Maritime',
      'Status': 'Active'
    }
  ];

  const wsLists = XLSX.utils.json_to_sheet(listExampleRows, { header: listHeaders });
  wsLists['!cols'] = [
    { wch: 25 }, // List Name
    { wch: 40 }, // Description
    { wch: 15 }, // Category
    { wch: 15 }  // Status
  ];
  XLSX.utils.book_append_sheet(wb, wsLists, 'Reference Lists');

  // Sheet 2: Values
  const valueHeaders = [
    'List Name',
    'Canonical Value',
    'Description',
    'Is Active'
  ];

  const valueExampleRows = [
    {
      'List Name': 'country_codes',
      'Canonical Value': 'US',
      'Description': 'United States of America',
      'Is Active': 'Yes'
    },
    {
      'List Name': 'country_codes',
      'Canonical Value': 'GB',
      'Description': 'United Kingdom',
      'Is Active': 'Yes'
    },
    {
      'List Name': 'country_codes',
      'Canonical Value': 'DE',
      'Description': 'Germany',
      'Is Active': 'Yes'
    },
    {
      'List Name': 'currency_codes',
      'Canonical Value': 'USD',
      'Description': 'US Dollar',
      'Is Active': 'Yes'
    },
    {
      'List Name': 'currency_codes',
      'Canonical Value': 'EUR',
      'Description': 'Euro',
      'Is Active': 'Yes'
    }
  ];

  const wsValues = XLSX.utils.json_to_sheet(valueExampleRows, { header: valueHeaders });
  wsValues['!cols'] = [
    { wch: 25 }, // List Name
    { wch: 25 }, // Canonical Value
    { wch: 40 }, // Description
    { wch: 10 }  // Is Active
  ];
  XLSX.utils.book_append_sheet(wb, wsValues, 'Values');

  // Sheet 3: Synonyms
  const synonymHeaders = [
    'List Name',
    'Canonical Value',
    'Synonym',
    'Is Approved'
  ];

  const synonymExampleRows = [
    {
      'List Name': 'country_codes',
      'Canonical Value': 'US',
      'Synonym': 'USA',
      'Is Approved': 'Yes'
    },
    {
      'List Name': 'country_codes',
      'Canonical Value': 'US',
      'Synonym': 'United States',
      'Is Approved': 'Yes'
    },
    {
      'List Name': 'country_codes',
      'Canonical Value': 'GB',
      'Synonym': 'UK',
      'Is Approved': 'Yes'
    },
    {
      'List Name': 'country_codes',
      'Canonical Value': 'GB',
      'Synonym': 'United Kingdom',
      'Is Approved': 'Yes'
    }
  ];

  const wsSynonyms = XLSX.utils.json_to_sheet(synonymExampleRows, { header: synonymHeaders });
  wsSynonyms['!cols'] = [
    { wch: 25 }, // List Name
    { wch: 25 }, // Canonical Value
    { wch: 30 }, // Synonym
    { wch: 12 }  // Is Approved
  ];
  XLSX.utils.book_append_sheet(wb, wsSynonyms, 'Synonyms');

  // Sheet 4: Instructions
  const instructions = [
    ['Reference Lists Import Template'],
    [''],
    ['Instructions:'],
    ['1. Fill in the data in the three sheets: "Reference Lists", "Values", and "Synonyms"'],
    ['2. Do not modify the column headers'],
    ['3. Remove or replace the example rows with your actual data'],
    ['4. Save the file and upload it through the import dialog'],
    [''],
    ['Sheet 1: Reference Lists'],
    [''],
    ['List Name (Required):'],
    ['  - Unique name for the reference list'],
    ['  - Maximum 255 characters'],
    ['  - Use lowercase with underscores (e.g., country_codes)'],
    [''],
    ['Description (Optional):'],
    ['  - Clear description of what this list contains'],
    ['  - Maximum 5000 characters'],
    [''],
    ['Category (Optional):'],
    ['  - Logical grouping for the list'],
    ['  - Examples: Geographic, Financial, Maritime'],
    [''],
    ['Status (Optional):'],
    ['  - Valid values: Active, Under Review, Deprecated'],
    ['  - Defaults to Active if not specified'],
    [''],
    ['Sheet 2: Values'],
    [''],
    ['List Name (Required):'],
    ['  - Must match a list name from the "Reference Lists" sheet'],
    ['  - Or must match an existing list in the database'],
    [''],
    ['Canonical Value (Required):'],
    ['  - The standardized value for this entry'],
    ['  - Must be unique within the same list'],
    ['  - Maximum 500 characters'],
    [''],
    ['Description (Optional):'],
    ['  - Human-readable description of the value'],
    [''],
    ['Is Active (Optional):'],
    ['  - Yes or No'],
    ['  - Defaults to Yes if not specified'],
    [''],
    ['Sheet 3: Synonyms'],
    [''],
    ['List Name (Required):'],
    ['  - Must match a list name from the "Reference Lists" sheet'],
    [''],
    ['Canonical Value (Required):'],
    ['  - Must match a value from the "Values" sheet'],
    ['  - Or must match an existing value in the database'],
    [''],
    ['Synonym (Required):'],
    ['  - Alternative value that maps to the canonical value'],
    ['  - Maximum 500 characters'],
    [''],
    ['Is Approved (Optional):'],
    ['  - Yes or No'],
    ['  - Defaults to Yes if not specified'],
    [''],
    ['Notes:'],
    ['  - If a List Name already exists, it will be UPDATED'],
    ['  - If a Value already exists in a list, it will be UPDATED'],
    ['  - Synonyms are always added (duplicates will be skipped)'],
    ['  - Maximum file size: 10MB'],
    ['  - Only .xlsx and .xls files are supported']
  ];

  const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);
  wsInstructions['!cols'] = [{ wch: 80 }];
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

  // Generate filename with current date
  const date = new Date().toISOString().split('T')[0];
  const filename = `reference_lists_template_${date}.xlsx`;

  // Write and trigger download
  XLSX.writeFile(wb, filename);
}

export function generateProcessingRulesTemplate() {
  const wb = XLSX.utils.book_new();

  // Define headers for the data sheet
  const headers = [
    'Rule_Name',
    'Description',
    'Rule_Type',
    'Rule_Language',
    'Input_Type',
    'Output_Type',
    'Rule_Code',
    'Test_Cases',
    'Status'
  ];

  // Create example rows
  const exampleRows = [
    {
      Rule_Name: 'validate_email',
      Description: 'Validates email format using regex',
      Rule_Type: 'validation',
      Rule_Language: 'python',
      Input_Type: 'string',
      Output_Type: 'string',
      Rule_Code: `def validate(value):
    import re
    if not value:
        return {'isValid': False, 'transformedValue': value, 'message': 'Email is required'}
    pattern = r'^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,}$'
    if re.match(pattern, str(value)):
        return {'isValid': True, 'transformedValue': value.lower(), 'message': None}
    return {'isValid': False, 'transformedValue': value, 'message': 'Invalid email format'}`,
      Test_Cases: JSON.stringify([
        { input: 'test@example.com', expected: { isValid: true } },
        { input: 'invalid-email', expected: { isValid: false } }
      ]),
      Status: 'Active'
    },
    {
      Rule_Name: 'normalize_phone',
      Description: 'Normalizes phone numbers to standard format',
      Rule_Type: 'transformation',
      Rule_Language: 'python',
      Input_Type: 'string',
      Output_Type: 'string',
      Rule_Code: `def validate(value):
    import re
    if not value:
        return {'isValid': False, 'transformedValue': value, 'message': 'Phone is required'}
    digits = re.sub(r'\\D', '', str(value))
    if len(digits) == 10:
        formatted = f'({digits[:3]}) {digits[3:6]}-{digits[6:]}'
        return {'isValid': True, 'transformedValue': formatted, 'message': None}
    return {'isValid': False, 'transformedValue': value, 'message': 'Invalid phone number'}`,
      Test_Cases: JSON.stringify([
        { input: '1234567890', expected: { isValid: true, transformedValue: '(123) 456-7890' } }
      ]),
      Status: 'Active'
    }
  ];

  // Create data sheet
  const wsData = XLSX.utils.json_to_sheet(exampleRows, { header: headers });

  // Set column widths
  wsData['!cols'] = [
    { wch: 30 },  // Rule_Name
    { wch: 50 },  // Description
    { wch: 15 },  // Rule_Type
    { wch: 15 },  // Rule_Language
    { wch: 15 },  // Input_Type
    { wch: 15 },  // Output_Type
    { wch: 80 },  // Rule_Code
    { wch: 50 },  // Test_Cases
    { wch: 12 }   // Status
  ];

  // Add data sheet to workbook
  XLSX.utils.book_append_sheet(wb, wsData, 'Processing Rules');

  // Create instructions sheet
  const instructions = [
    ['Processing Rules Import Template'],
    [''],
    ['Instructions:'],
    ['1. Fill in the data in the "Processing Rules" sheet'],
    ['2. Do not modify the column headers'],
    ['3. Remove or replace the example rows with your actual data'],
    ['4. Save the file and upload it through the import dialog'],
    [''],
    ['Column Descriptions:'],
    [''],
    ['Rule_Name (Required):'],
    ['  - Unique name for the processing rule'],
    ['  - Must be unique across all rules'],
    ['  - Maximum 255 characters'],
    ['  - Use lowercase with underscores (e.g., validate_email)'],
    [''],
    ['Description (Optional):'],
    ['  - Clear description of what this rule does'],
    ['  - Maximum 5000 characters'],
    [''],
    ['Rule_Type (Required):'],
    ['  - Type of processing rule'],
    ['  - Valid values: validation, transformation'],
    [''],
    ['Rule_Language (Required):'],
    ['  - Programming language of the rule code'],
    ['  - Valid values: python, javascript'],
    [''],
    ['Input_Type (Optional):'],
    ['  - Expected input data type'],
    ['  - Examples: utf8, int32, date32, any'],
    [''],
    ['Output_Type (Optional):'],
    ['  - Output data type after processing'],
    ['  - Examples: utf8, int32, bool, any'],
    [''],
    ['Rule_Code (Required):'],
    ['  - The actual code that implements the rule'],
    ['  - Must define a function called "validate" that takes a value'],
    ['  - Must return an object with: isValid (boolean), transformedValue, message'],
    [''],
    ['Test_Cases (Optional):'],
    ['  - JSON array of test cases'],
    ['  - Format: [{"input": "value", "expected": {"isValid": true}}]'],
    [''],
    ['Status (Optional):'],
    ['  - Rule status'],
    ['  - Valid values: Active, Inactive, Draft'],
    ['  - Defaults to Draft if not specified'],
    [''],
    ['Notes:'],
    ['  - If a Rule_Name already exists, it will be UPDATED'],
    ['  - Maximum file size: 10MB'],
    ['  - Maximum rows: 1,000 rules'],
    ['  - Only .xlsx and .xls files are supported']
  ];

  const wsInstructions = XLSX.utils.aoa_to_sheet(instructions);

  // Set column width for instructions
  wsInstructions['!cols'] = [{ wch: 80 }];

  // Add instructions sheet to workbook
  XLSX.utils.book_append_sheet(wb, wsInstructions, 'Instructions');

  // Generate filename with current date
  const date = new Date().toISOString().split('T')[0];
  const filename = `processing_rules_template_${date}.xlsx`;

  // Write and trigger download
  XLSX.writeFile(wb, filename);
}
